// Shared option builders for the grouped, searchable MultiFacet / FacetMenu popover (search, All / None, group headers).
// Pure: callers pass the label resolver so these work with live (hydrated) category constants. Reuses groupOptions from
// Inventory Movements (group order = owner category order, then sub-group, then label).
import i18n from '../i18n';
import { accountClass } from '../utils/accountClass';
import { groupOptions } from '../pages/InventoryMovements/movementsModel';

const lc = (c) => String(c || '').toLowerCase();
const nm = (o) => (o.name ?? o.label ?? o.id ?? '');

// items [{id|value, name|label, category, sub?}] -> constant > (item-category) > item
export const itemFacetGroups = (items, labelOf, order = []) =>
  groupOptions((items || []).map((i) => ({ value: i.id ?? i.value, label: nm(i), cat: lc(i.category), sub: i.sub || i.item_category_labels?.name })), order, labelOf);

// bands [{id,name,phase?,band_status?,farm_name?}] -> lifecycle PHASE only (owner 46860): Planned > Growing > Laying > Retired.
// No farm / site level, no status level. No phase on the row: derive from band_status (queued / released), else ungrouped.
const PHASES = ['not_started', 'growing', 'laying', 'retired'];
const phaseOf = (b) => b.phase || ({ queued: 'not_started', released: 'retired', cancelled: 'retired' })[b.band_status] || undefined;
export const isRetiredBand = (b) => phaseOf(b || {}) === 'retired';
export const bandPhaseLabel = (p) => i18n.t(`bandPhase.${p}`);
// Attach the phase group to ready-made options [{value: bandId, label, ...}] (e.g. report facets with counts), phase order then label.
// { hideRetired, keep }: filter bars hide the Retired phase unless the "Show inactive" toggle is on; ids in `keep` (selected values) always stay.
export function withBandPhase(opts, bands, { hideRetired = false, keep = [] } = {}) {
  const by = new Map((bands || []).map((b) => [b.id, b]));
  const kept = new Set([].concat(keep || []));
  if (hideRetired) opts = (opts || []).filter((o) => kept.has(o.value) || phaseOf(by.get(o.value) || {}) !== 'retired');
  const rank = (b) => { const i = PHASES.indexOf(phaseOf(b)); return i < 0 ? PHASES.length : i; };
  return (opts || [])
    .map((o) => { const b = by.get(o.value) || {}; return { ...o, group: PHASES.includes(phaseOf(b)) ? bandPhaseLabel(phaseOf(b)) : undefined, _r: rank(b) }; })
    .sort((a, b) => a._r - b._r || String(a.label).localeCompare(String(b.label)))
    .map(({ _r, ...o }) => o);
}
export const bandFacetGroups = (bands, o) => withBandPhase((bands || []).map((b) => ({ value: b.id, label: nm(b) })), bands, o);

// accounts [{id,name,type?,classification?}] -> class group (Asset > Liability > Equity > Revenue > Expense > Other); input (Balance-Sheet) order kept inside a class.
const CLASS_ORDER = ['Asset', 'Liability', 'Equity', 'Revenue', 'Expense', 'Other'];
export const accountFacetGroups = (accounts, labelFn) =>
  (accounts || [])
    .map((a) => ({ value: a.id, label: labelFn ? labelFn(a) : nm(a), group: accountClass(a) }))
    .map((o, i) => ({ o, i }))
    .sort((x, y) => CLASS_ORDER.indexOf(x.o.group) - CLASS_ORDER.indexOf(y.o.group) || x.i - y.i)
    .map(({ o }) => o);

// storages [{id,name,constant_key}] (+ bands [{id,name,phase?}] under 'birds', sub-grouped by lifecycle phase) -> constant > node
export function storageFacetGroups(storages, labelOf, order = [], bands = []) {
  const out = groupOptions((storages || []).map((s) => ({ value: s.id, label: nm(s), cat: lc(s.constant_key) })), order, labelOf);
  if (!bands?.length) return out;
  const rank = (g) => { const i = order.findIndex((c) => labelOf(c) === g); return i < 0 ? 999 : i; };
  const birds = bandFacetGroups(bands).map((o) => ({ ...o, group: labelOf('birds'), subgroup: o.group }));
  const at = out.findIndex((o) => rank(o.group) > rank(labelOf('birds')));
  return at < 0 ? [...out, ...birds] : [...out.slice(0, at), ...birds, ...out.slice(at)];
}

// scope target of a task / activity: band -> phase groups, farm -> site groups, site / location -> flat
export const targetFacetGroups = (type, list) => type === 'band' ? bandFacetGroups(list) : type === 'farm' ? farmFacetGroups(list) : (list || []).map((o) => ({ value: o.id, label: nm(o) })).sort((a, b) => a.label.localeCompare(b.label));

// farms [{id,name,site_name?}] -> site group
export const farmFacetGroups = (farms) =>
  (farms || []).map((f) => ({ value: f.id, label: nm(f), group: f.site_name || undefined })).sort((a, b) => String(a.group || '').localeCompare(String(b.group || '')) || a.label.localeCompare(b.label));

// storage-item nodes (report lines {node_id,node_name,node_type,site_name}) -> type (Storages / Bands) > site > node
export function nodeFacetGroups(lines, typeLabel = (t) => t) {
  const m = new Map();
  for (const l of lines || []) if (l.node_id && !m.has(l.node_id)) m.set(l.node_id, l);
  return [...m.values()]
    .map((l) => ({ value: l.node_id, label: l.node_name || l.node_id, group: typeLabel(l.node_type === 'band' ? 'band' : 'storage'), subgroup: l.site_name || undefined }))
    .sort((a, b) => (a.group === b.group ? 0 : a.group.localeCompare(b.group)) || String(a.subgroup || '').localeCompare(String(b.subgroup || '')) || a.label.localeCompare(b.label));
}
